import { Link, Navigate, useLocation, useParams } from "react-router-dom"
import { cn } from "@/lib/utils"
import { LibraryPage } from "@/components/LibraryPage"
import { TemplatesPage } from "@/components/TemplatesPage"
import { PromptsPage } from "@/components/PromptsPage"
import { CompanyProfileForm } from "@/components/knowledge/CompanyProfileForm"
import {
  DEFAULT_KNOWLEDGE_TAB,
  KNOWLEDGE_TABS,
  isKnowledgeTab,
  knowledgeTabPath,
  type KnowledgeTab,
} from "@/components/knowledge/knowledge-tabs"

function KnowledgeTabContent({ tab }: { tab: KnowledgeTab }) {
  switch (tab) {
    case "library":
      return <LibraryPage />
    case "company-profile":
      return (
        <div className="h-full overflow-auto">
          <div className="max-w-4xl px-6 pb-4 pt-6">
            <CompanyProfileForm />
          </div>
        </div>
      )
    case "templates":
      return <TemplatesPage />
    case "prompts":
      return <PromptsPage />
  }
}

/**
 * Knowledge hub (R4): one home for Library, Company profile, Templates and
 * Saved prompts. The URL holds the tab (`/app/knowledge/<tab>`) so each one
 * deep-links; each tab renders today's page unchanged.
 */
export function KnowledgePage() {
  const { tab } = useParams<{ tab?: string }>()
  const location = useLocation()

  if (!isKnowledgeTab(tab)) {
    return (
      <Navigate
        replace
        to={{
          pathname: knowledgeTabPath(DEFAULT_KNOWLEDGE_TAB),
          search: location.search,
          hash: location.hash,
        }}
      />
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* Primary-tab underline sub-nav (design system: Tabs → Primary tabs) */}
      <nav
        aria-label="Knowledge"
        className="flex h-11 shrink-0 items-end gap-4 overflow-x-auto border-b border-border bg-card px-4"
      >
        {KNOWLEDGE_TABS.map((item) => {
          const isActive = item.id === tab
          return (
            <Link
              key={item.id}
              to={knowledgeTabPath(item.id)}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "-mb-px whitespace-nowrap border-b-2 px-2 py-2.5 text-sm transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isActive
                  ? "border-foreground font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {item.label}
            </Link>
          )
        })}
      </nav>
      <div className="min-h-0 flex-1 overflow-hidden">
        <KnowledgeTabContent tab={tab} />
      </div>
    </div>
  )
}
